/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { notifications } from '@utils/notifications';
import CartQL from './gql/stage/cart.mainEndpoint';
import ShopsQuery from './gql/query/shops.stageEndpoint';

import Config from '@root/wrapperConfig';
import type * as MainTypes from './schema/main';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type * as CartTypes from './schema/cart';
import type * as StageTypes from './schema/stage';

import { configValuesKeys, configValues } from './enums/configValues.enum';
import PersonalAccQuery from './gql/stage/config.mainEndpoint';
import { getLocalStorageItem } from '../utils/getLocalStorageItem';
import type { DocumentNode } from 'graphql';
import type { GqlResponse } from '../types';

const prepareConfigValueObj = (
  list: typeof configValuesKeys
): MainTypes.ConfigValueListQueryVariables => {
  const result = configValuesKeys.reduce(
    (acc, item) => ({ ...acc, [item]: list.includes(item) }),
    configValues
  );
  return result;
};

export default new (class {
  // Only GraphQL
  server: string;

  proxyurl: string;

  constructor() {
    this.server = Config.server.api;

    this.proxyurl = '';
  }

  pickupPointById = async (
    variables: MainTypes.PickupPointByIdQueryVariables
  ) =>
    await this.callMethod<
      MainTypes.PickupPointByIdQuery,
      MainTypes.PickupPointByIdQueryVariables
    >(CartQL.pickupPointById, variables);

  pickupPoints = async (variables: MainTypes.PickupPointsQueryVariables) =>
    await this.callMethod<
      MainTypes.PickupPointsQuery,
      MainTypes.PickupPointsQueryVariables
    >(CartQL.pickupPoints, variables);

  deliveryAddresses = async (
    variables: MainTypes.DeliveryAddressesQueryVariables
  ) =>
    await this.callMethod<
      MainTypes.DeliveryAddressesQuery,
      MainTypes.DeliveryAddressesQueryVariables
    >(CartQL.deliveryAddresses, variables);

  getAmountStores = async (
    variables: MainTypes.GetAmountStoresQueryVariables
  ) =>
    await this.callMethod<
      MainTypes.GetAmountStoresQuery,
      MainTypes.GetAmountStoresQueryVariables
    >(CartQL.getAmountStores, variables);

  nearestDelivery = async (
    variables: MainTypes.NearestDeliveryQueryVariables
  ) =>
    await this.callMethod<
      MainTypes.NearestDeliveryQuery,
      MainTypes.NearestDeliveryQueryVariables
    >(CartQL.nearestDelivery, variables);

  getDeliveryIntervals = async (
    variables: MainTypes.GetDeliveryIntervalsQueryVariables
  ) =>
    await this.callMethod<
      MainTypes.GetDeliveryIntervalsQuery,
      MainTypes.GetDeliveryIntervalsQueryVariables
    >(CartQL.getDeliveryIntervals, variables);

  deliveryIntervals = async (
    variables: MainTypes.FlexibleDeliveryIntervalsQueryVariables
  ) =>
    await this.callMethod<
      MainTypes.FlexibleDeliveryIntervalsQuery,
      MainTypes.FlexibleDeliveryIntervalsQueryVariables
    >(CartQL.deliveryIntervals, variables);

  createOrderFromCart = async (
    variables: MainTypes.CreateOrderFromCartMutationVariables
  ) => {
    if (localStorage.getItem('cashback_uid') !== null) {
      variables.data.cashbackId = localStorage.getItem('cashback_uid');
      localStorage.removeItem('cashback_uid');
    }

    return await this.callMethod<
      MainTypes.CreateOrderFromCartMutation,
      MainTypes.CreateOrderFromCartMutationVariables
    >(CartQL.createOrderFromCart, variables, undefined, 'create-order');
  };

  afterOrderPromocode = async (
    variables: MainTypes.AfterOrderPromocodeQueryVariables
  ) =>
    await this.callMethod<
      MainTypes.AfterOrderPromocodeQuery,
      MainTypes.AfterOrderPromocodeQueryVariables
    >(CartQL.afterOrderPromocode, variables);

  getPaymentOptions = async (
    variables: MainTypes.GetPaymentOptionsQueryVariables
  ) =>
    await this.callMethod<
      MainTypes.GetPaymentOptionsQuery,
      MainTypes.GetPaymentOptionsQueryVariables
    >(CartQL.getPaymentOptions, variables);

  updateCart = async (variables: MainTypes.UpdateCartMutationVariables) => {
    if (variables.data) {
      if (variables.data.deliveryIntervalStart) {
        variables.data.deliveryIntervalStart =
          +variables.data.deliveryIntervalStart;
      }

      if (variables.data.deliveryIntervalEnd) {
        variables.data.deliveryIntervalEnd =
          +variables.data.deliveryIntervalEnd;
      }

      if (variables.data.dadataId) {
        variables.data.dadataId = +variables.data.dadataId;
      }
    }
    return await this.callMethod<
      MainTypes.UpdateCartMutation,
      MainTypes.UpdateCartMutationVariables
    >(CartQL.updateCart, variables);
  };

  todayLatestInterval = async (
    variables: MainTypes.TodayLatestIntervalQueryVariables
  ) =>
    await this.callMethod<
      MainTypes.TodayLatestIntervalQuery,
      MainTypes.TodayLatestIntervalQueryVariables
    >(CartQL.todayLatestInterval, variables);

  addItemsToCartFromOrder = async (
    variables: MainTypes.AddItemsToCartFromOrderMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.AddItemsToCartFromOrderMutation,
      MainTypes.AddItemsToCartFromOrderMutationVariables
    >(CartQL.addItemsToCartFromOrder, variables);

  addItemsToCartFromTemplate = async (
    variables: MainTypes.AddItemsToCartFromTemplateMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.AddItemsToCartFromTemplateMutation,
      MainTypes.AddItemsToCartFromTemplateMutationVariables
    >(CartQL.addItemsToCartFromTemplate, variables);

  checkMaxAvailableQuantityInOneOrder = async (
    variables: MainTypes.CheckMaxAvailableQuantityInOneOrderQueryVariables
  ) =>
    await this.callMethod<
      MainTypes.CheckMaxAvailableQuantityInOneOrderQuery,
      MainTypes.CheckMaxAvailableQuantityInOneOrderQueryVariables
    >(CartQL.checkMaxAvailableQuantityInOneOrder, variables);

  createCartFromOrder = async (
    variables: MainTypes.CreateCartFromOrderMutationVariables
  ) => {
    variables.orderId = +variables.orderId;
    return await this.callMethod<
      MainTypes.CreateCartFromOrderMutation,
      MainTypes.CreateCartFromOrderMutationVariables
    >(CartQL.createCartFromOrder, variables);
  };

  saveChangesToOrder = async (
    variables: MainTypes.SaveChangesToOrderMutationVariables
  ) => {
    variables.orderId = +variables.orderId;
    return await this.callMethod<
      MainTypes.SaveChangesToOrderMutation,
      MainTypes.SaveChangesToOrderMutationVariables
    >(CartQL.saveChangesToOrder, variables);
  };

  undoChangesToOrder = async (
    variables: MainTypes.UndoChangesToOrderMutationVariables
  ) => {
    variables.orderId = +variables.orderId;
    return await this.callMethod<
      MainTypes.UndoChangesToOrderMutation,
      MainTypes.UndoChangesToOrderMutationVariables
    >(CartQL.undoChangesToOrder, variables);
  };

  cancelOrder = async (variables: MainTypes.CancelOrderMutationVariables) => {
    variables.orderId = +variables.orderId;
    return await this.callMethod<
      MainTypes.CancelOrderMutation,
      MainTypes.CancelOrderMutationVariables
    >(CartQL.cancelOrder, variables);
  };

  mergeCarts = async (variables: MainTypes.MergeCartsMutationVariables) =>
    await this.callMethod<
      MainTypes.MergeCartsMutation,
      MainTypes.MergeCartsMutationVariables
    >(CartQL.mergeCarts, variables);

  updateCartLoyaltyProgram = async (
    variables: MainTypes.UpdateCartLoyaltyProgramMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.UpdateCartLoyaltyProgramMutation,
      MainTypes.UpdateCartLoyaltyProgramMutationVariables
    >(CartQL.updateCartLoyaltyProgram, variables);

  checkCartChanges = async (
    variables: MainTypes.CheckCartChangesQueryVariables
  ) =>
    await this.callMethod<
      MainTypes.CheckCartChangesQuery,
      MainTypes.CheckCartChangesQueryVariables
    >(CartQL.checkCartChanges, variables);

  configValueList = async (list: typeof configValuesKeys) =>
    await this.callMethod<
      MainTypes.ConfigValueListQuery,
      MainTypes.ConfigValueListQueryVariables
    >(PersonalAccQuery.configValueList, prepareConfigValueObj(list));

  configValue = async (variables: MainTypes.ConfigValueQueryVariables) =>
    await this.callMethod<
      MainTypes.ConfigValueQuery,
      MainTypes.ConfigValueQueryVariables
    >(CartQL.configValue, variables);

  confirmProfileBySMS = async (
    variables: MainTypes.ConfirmProfileBySmsMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.ConfirmProfileBySmsMutation,
      MainTypes.ConfirmProfileBySmsMutationVariables
    >(CartQL.confirmProfileBySMS, variables);

  isPersonRestrincted = async () =>
    await this.callMethod<MainTypes.IsPersonRestrinctedQuery>(
      CartQL.isPersonRestrincted
    );

  getShopsByCartToken = async (
    variables: StageTypes.GetShopsByCartTokenQueryVariables
  ) =>
    await this.callMethod<
      StageTypes.GetShopsByCartTokenQuery,
      StageTypes.GetShopsByCartTokenQueryVariables
    >(ShopsQuery.getShopsByCartToken, variables);

  async callMethod<T, K = void>(
    query: DocumentNode,
    variables?: K,
    cookieId?: string,
    xQueryType?: string
  ): Promise<T> {
    const headerToken = cookieId ?? getLocalStorageItem<string>('headerToken');
    const xStoreId = getLocalStorageItem<string>('x-store-id');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-People-Token': headerToken,
      'x-store-id': xStoreId,
      'x-request-id': `${Date.now()}::${Math.trunc(Math.random() * 10000)}`
    };
    if (xQueryType) {
      headers['x-query-type'] = xQueryType;
    }
    return await new Promise((resolve, reject) => {
      fetch(this.proxyurl + this.server, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          query: query.loc?.source.body,
          variables
        })
      })
        .then(async response => await response.json())
        .then(async (data: GqlResponse<T>) => {
          // if (query.indexOf('createOrderFromCart') !== -1 && !data.data.createOrderFromCart.success) {
          //   notifications(data.data.createOrderFromCart.error);
          // }
          let message = '';

          if (typeof data.data !== 'undefined') {
            for (const prop in data.data) {
              const methodProp = data.data[prop];

              if (
                methodProp &&
                typeof methodProp.warning !== 'undefined' &&
                methodProp.warning != null &&
                methodProp.warning.length !== 0
              ) {
                message = data.data[prop].warning;
              }

              if (
                methodProp &&
                typeof methodProp.error !== 'undefined' &&
                methodProp.error != null &&
                methodProp.error.length !== 0
              ) {
                message = data.data[prop].error;
              }
            }

            if (message.length !== 0) {
              notifications(message, 'Внимание');
            }
          }

          if (
            typeof data.data === 'undefined' ||
            data.data === null ||
            typeof data.errors !== 'undefined'
          ) {
            return await Promise.reject(data.errors);
          }

          resolve(data.data);
        })
        .catch(e => {
          notifications(e);
          reject(e);
        });
    });
  }
})();
